import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { keyWithPrincipal } from "@/queries/principal-key";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import {  type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import {
    ListPrebuildsRequest,
    ListPrebuildsRequest_Filter,
    type ListPrebuildsResponse,
    type Prebuild,
    CreatePrebuildRequest,
    PrebuildSpec,
} from "gitpod-next-api/gitpod/v1/prebuild_pb";

export const prebuildQueryKeys = {
    listForProject: (projectId?: string) => keyWithPrincipal(["prebuilds", "list", { projectId }]),
};

export type PlainPrebuild = PlainMessage<Prebuild>;

export const useProjectPrebuilds = (projectId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: prebuildQueryKeys.listForProject(projectId),
        queryFn: async () => {
            if (!projectId) {
                return { prebuilds: [], pagination: undefined };
            }

            const prebuilds = await autoPaginate<ListPrebuildsResponse, ListPrebuildsRequest, Prebuild>(
                (params) => api.prebuildService.listPrebuilds(params),
                new ListPrebuildsRequest({
                    filter: new ListPrebuildsRequest_Filter({
                        projectIds: [projectId],
                    }),
                }),
                (response) => response.prebuilds,
            );

            return {
                prebuilds: prebuilds.map(toPlainPrebuild),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!projectId,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60, // 1 hour

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const toPlainPrebuild = (prebuild: Prebuild): PlainPrebuild => {
    return toPlainMessage(prebuild);
};

export type CreatePrebuildParams = {
    projectId: string;
    environmentClassId?: string;
};

export const useCreatePrebuild = (): UseMutationResult<PlainPrebuild, Error, CreatePrebuildParams, unknown> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation<PlainPrebuild, Error, CreatePrebuildParams>({
        mutationFn: async (params: CreatePrebuildParams) => {
            const request = new CreatePrebuildRequest({
                projectId: params.projectId,
                environmentClassId: params.environmentClassId,
                spec: new PrebuildSpec({}),
            });
            const { prebuild } = await api.prebuildService.createPrebuild(request);
            if (!prebuild) {
                throw new Error("Failed to create prebuild");
            }
            return toPlainPrebuild(prebuild);
        },
        onSuccess: (_created, variables) => {
            // Refresh the list of prebuilds for this project
            queryClient
                .invalidateQueries({
                    queryKey: prebuildQueryKeys.listForProject(variables.projectId),
                })
                .catch(() => void 0);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};
